import styled, { css } from 'styled-components';
import { both } from 'ramda';
import { BUTTON_MODAL_TYPE, BUTTON_PRIMARY_TYPE, BUTTON_SECONDARY_TYPE } from './button.constants';
import { Icon as IconBase } from '../icon';
import {
  themeBorder,
  themeColor,
  themeShadow,
  whenThemePropEqualsExpectedValue,
  whenThemePropEqualsTrue,
} from '../../../theme/getters';
import { smallSpacedHeading } from '../../../theme/typography';
import { DotLoader } from '../DotLoader';
import { fillAbsolute, noTapSelection } from '../../../theme/mixins';

const whenIsDisabled = whenThemePropEqualsTrue('disabled');
const whenIsDark = whenThemePropEqualsTrue('dark');
const whenIsPrimaryType = whenThemePropEqualsExpectedValue('theme', BUTTON_PRIMARY_TYPE);
const whenIsSecondaryType = whenThemePropEqualsExpectedValue('theme', BUTTON_SECONDARY_TYPE);
const whenIsModalType = whenThemePropEqualsExpectedValue('theme', BUTTON_MODAL_TYPE);
const whenIsPrimaryDark = both(whenIsPrimaryType, whenIsDark);

const getDisabledModalContainerStyles = whenIsModalType(
  () => css`
    background: ${themeColor('border')};
  `
);

const getDisabledContainerStyles = whenIsDisabled(
  () => css`
    pointer-events: none;
    color: ${themeColor('disabled')};
    &:after {
      ${themeBorder('buttonDisabled')}
    }

    ${getDisabledModalContainerStyles};
  `
);

const getDarkContainerStyles = whenIsDark(
  () => css`
    color: ${themeColor('white')};
    background: transparent;
    border-radius: 0;
  `
);

const getSecondaryContainerStyles = whenIsSecondaryType(
  () => css`
    justify-content: flex-start;
    border-radius: 30px;
    box-shadow: none;
    width: auto;
    padding: 10px 24px;

    &:after {
      display: none;
    }

    ${getDarkContainerStyles}
  `
);

const getModalContainerStyles = whenIsModalType(
  () => css`
    padding: 24px;
    box-shadow: none;

    &:after {
      display: none;
    }
  `
);

const getDisabledModalTextStyles = whenIsModalType(
  () => css`
    opacity: 0.7;
  `
);

const getDisabledTextStyles = whenIsDisabled(
  () => css`
    color: ${themeColor('disabled')};
    ${getDisabledModalTextStyles};
  `
);

const getDarkTextStyles = whenIsDark(
  () => css`
    color: ${themeColor('white')};
  `
);

const getSecondaryTextStyles = whenIsSecondaryType(
  () => css`
    ${smallSpacedHeading}
    ${getDarkTextStyles}
  `
);

const getPrimaryDarkDisabledStyles = whenIsDisabled(
  () => css`
    background: ${themeColor('border')};
  `
);

const getPrimaryDarkStyles = whenIsPrimaryDark(
  () => css`
    background: ${themeColor('primary')};
    color: ${themeColor('white')};
    ${getPrimaryDarkDisabledStyles};
  `
);

export const Container = styled.button`
  ${noTapSelection};
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border: 0;
  box-shadow: none;
  background: ${themeColor('white')};
  padding: 27px;
  width: 100%;
  ${props =>
    props.wide &&
    css`
      min-width: 552px;
    `};
  cursor: pointer;

  ${themeShadow('primary')};

  &:after {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    content: '';
    ${themeBorder('button')};
    pointer-events: none;
  }

  ${smallSpacedHeading};
  text-transform: uppercase;

  ${getPrimaryDarkStyles};
  ${getSecondaryContainerStyles};
  ${getModalContainerStyles};
  ${getDisabledContainerStyles};
`;

export const Text = styled.span`
  flex: 1;
  text-align: center;
  text-transform: uppercase;

  ${getSecondaryTextStyles};
  ${getDisabledTextStyles};
`;

export const Icon = styled(IconBase).attrs(() => ({
  size: 12,
}))`
  margin-left: 10px;
`;

export const LoaderWrapper = styled.div`
  ${fillAbsolute};
  background: ${themeColor('white')};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Loader = styled(DotLoader)``;
